import { PhoneCall, Workflow, Mail, MessageSquare, Megaphone } from 'lucide-react';
import type { NicheConfig } from './types';

// Lokalne landing pages pod frazy "automatyzacja AI + miasto" (programmatic-lite).
// Reuse calego NicheLandingPage (/lp/:slug). Wspolne sekcje, zmienne tylko miasto + meta + FAQ.

const BOOKING = 'https://calendar.app.google/4V6jKtrVUtMihmoE9';
const AREA = ['Gdynia', 'Gdańsk', 'Sopot', 'Trójmiasto', 'Pomorze'];

const sharedServices = (locative: string): NicheConfig['services'] => ({
  sectionEyebrow: 'Co wdrażam',
  sectionTitle: 'Systemy AI, które zdejmują z firmy ręczną robotę',
  items: [
    {
      icon: PhoneCall,
      title: 'Voicebot AI 24/7',
      description: `Recepcjonista AI odbiera telefony ${locative} w niecałą minutę, odpowiada na pytania i umawia spotkania. Klient dzwoni o 22:00, telefon odebrany, termin w kalendarzu.`,
    },
    {
      icon: Workflow,
      title: 'Automatyzacje n8n',
      description: 'Łączę Twoje narzędzia (formularze, arkusze, CRM, Slack) w jeden przepływ. Koniec ręcznego przepisywania danych i zgubionych leadów.',
    },
    {
      icon: Mail,
      title: 'Cold email AI',
      description: 'Spersonalizowane kampanie outbound, które dowożą spotkania. Dla Kognity 2 000 maili dało 2 nowych klientów i kilkukrotny zwrot z inwestycji.',
    },
    {
      icon: MessageSquare,
      title: 'RAG i obsługa klienta',
      description: 'System odpowiada na powtarzalne pytania z bazy wiedzy firmy. U klienta e-commerce 1 200 ticketów miesięcznie, 95% obsłużone automatycznie.',
    },
    {
      icon: Megaphone,
      title: 'Lead generation',
      description: 'Reklamy, landing page i quiz kwalifikujący. AI ocenia leady, Ty rozmawiasz tylko z gotowymi do zakupu.',
    },
  ],
});

const sharedWhyMe = (locative: string): NicheConfig['whyMe'] => ({
  sectionEyebrow: 'Dlaczego DAIEN',
  sectionTitle: `Lokalnie, ${locative}, na sprawdzonych systemach`,
  body: `Prowadzę DAIEN z Gdyni i każdy system, który u Ciebie wdrożę, najpierw chodzi u mnie. Voicebot odbiera moje telefony, automatyzacje generują moje leady. Pracuję z firmami ${locative} i w całej Polsce, więc znam realia lokalnego rynku, nie sprzedaję slajdami z Warszawy.`,
  points: [
    { title: 'Lokalny w Trójmieście', description: `Działam ${locative} (Gdynia, Gdańsk, Sopot). Spotkanie na żywo, nie tylko przez Google Meet.` },
    { title: 'Testowane na żywo', description: 'Każdy system najpierw odpalam u siebie w DAIEN. U klienta wdrażam to, co już sprawdzone.' },
    { title: 'Konkret w liczbach', description: 'Pokazuję ile kosztuje setup, ile retainer i jaki realny efekt po 30, 60 i 90 dniach.' },
    { title: 'Dane zostają u Ciebie', description: 'Stack open-source-first (n8n, Retell, Supabase). Bez vendor lock-in.' },
  ],
});

const faqFor = (locative: string): NicheConfig['faq'] => [
  {
    q: `Czy DAIEN obsługuje firmy ${locative}?`,
    a: `Tak. DAIEN działa z Gdyni i obsługuje firmy w całym Trójmieście (Gdynia, Gdańsk, Sopot) oraz zdalnie w całej Polsce. Spotkania ${locative} mogą odbyć się na żywo.`,
  },
  {
    q: 'Ile kosztuje wdrożenie systemu AI?',
    a: 'Setup zaczyna się od kilku tysięcy złotych zależnie od zakresu (voicebot, automatyzacja, cold email), plus miesięczny koszt infrastruktury. Pełną wycenę dostajesz po krótkim quizie kwalifikacyjnym.',
  },
  {
    q: 'Jak długo trwa wdrożenie?',
    a: 'Standardowo 2-4 tygodnie od decyzji do produkcji. Lead gen z landingiem to około 2 tygodnie, voicebot z bazą wiedzy 3 tygodnie, RAG z integracjami 4 tygodnie.',
  },
  {
    q: 'Czym to się różni od agencji marketingowej?',
    a: 'Agencja sprzedaje godziny i raporty. DAIEN buduje systemy, które działają 24/7 bez Twojej obsługi: voiceboty, automatyzacje n8n, RAG. Dane i kod zostają u Ciebie, bez vendor lock-in.',
  },
];

interface LocalInput {
  slug: string;
  city: string;
  locative: string;
  title: string;
  titleAccent: string;
  metaTitle: string;
  metaDescription: string;
}

function makeLocal(i: LocalInput): NicheConfig {
  return {
    slug: i.slug,
    niche: i.city,
    meta: { title: i.metaTitle, description: i.metaDescription },
    areaServed: AREA,
    hero: {
      eyebrow: `Trójmiasto · ${i.city}`,
      title: i.title,
      titleAccent: i.titleAccent,
      subtitle: i.metaDescription,
      ctaLabel: 'Umów 30-min rozmowę',
      ctaHref: '#booking',
    },
    services: sharedServices(i.locative),
    whyMe: sharedWhyMe(i.locative),
    cta: {
      title: 'Porozmawiajmy 30 minut',
      description: `Pokażę systemy, podyskutujemy o Twojej firmie ${i.locative}, zaproponuję rozwiązanie. Bez sprzedaży na siłę.`,
      bookingUrl: BOOKING,
    },
    faq: faqFor(i.locative),
  };
}

export const localConfigs: NicheConfig[] = [
  makeLocal({
    slug: 'automatyzacja-ai-gdynia',
    city: 'Gdynia',
    locative: 'w Gdyni',
    title: 'Automatyzacja AI dla firm',
    titleAccent: 'w Gdyni',
    metaTitle: 'Automatyzacja AI dla firm w Gdyni - DAIEN',
    metaDescription:
      'DAIEN buduje systemy AI dla firm w Gdyni: voiceboty 24/7, automatyzacje n8n, cold email i RAG. Mniej ręcznej roboty, więcej leadów i czasu.',
  }),
  makeLocal({
    slug: 'agencja-ai-gdansk',
    city: 'Gdańsk',
    locative: 'w Gdańsku',
    title: 'Agencja AI i automatyzacji',
    titleAccent: 'w Gdańsku',
    metaTitle: 'Agencja AI i automatyzacji w Gdańsku - DAIEN',
    metaDescription:
      'DAIEN to agencja AI w Gdańsku: voiceboty, automatyzacje n8n, cold email i RAG dla firm usługowych. Sprawdzone systemy, mierzalny efekt.',
  }),
  makeLocal({
    slug: 'automatyzacja-ai-trojmiasto',
    city: 'Trójmiasto',
    locative: 'w Trójmieście',
    title: 'Automatyzacja AI i voiceboty',
    titleAccent: 'w Trójmieście',
    metaTitle: 'Automatyzacja AI i voiceboty w Trójmieście - DAIEN',
    metaDescription:
      'DAIEN buduje systemy AI dla firm w Trójmieście (Gdynia, Gdańsk, Sopot): voiceboty 24/7, automatyzacje n8n, cold email i RAG. Mniej ręcznej roboty, więcej leadów.',
  }),
  makeLocal({
    slug: 'automatyzacja-ai-sopot',
    city: 'Sopot',
    locative: 'w Sopocie',
    title: 'Automatyzacja AI dla firm',
    titleAccent: 'w Sopocie',
    metaTitle: 'Automatyzacja AI dla firm w Sopocie - DAIEN',
    metaDescription:
      'Telefon po godzinach bez odpowiedzi to klient, który dzwoni do konkurencji. DAIEN z Gdyni wdraża firmom w Sopocie voiceboty 24/7, automatyzacje i aplikacje szyte na miarę, żeby żadne zapytanie nie przepadło.',
  }),
];
